import { ButtonsGroup } from '@billy/design-system'
import { ComponentType, ReactElement, useCallback } from 'react'
import { useNavigate } from 'react-router'

import { DeleteQuoteModal } from '@features/invoices/elements/DeleteQuoteModal'
import { QuoteActionType } from '@features/invoices/enums/quoteActionType'
import { QuoteRelatedDraftInvoiceType } from '@features/invoices/enums/quoteRelatedDraftInvoiceType'
import { getQuoteDepositInvoiceDraft } from '@features/invoices/utils/depositInvoices/getQuoteDepositInvoiceDraft'
import { getQuoteRegularInvoiceDraft } from '@features/invoices/utils/depositInvoices/getQuoteRegularInvoiceDraft'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { ModalId } from '../../../../../../enums/modalId'
import { useSegment } from '../../../../../../hooks'
import { getOrganizationViewUrl } from '../../../../../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../../../../../utils/routing/reactRoute'
import { useQuote } from '../../contexts/quoteContext'
import { ConvertQuoteActions } from './elements/ConvertQuoteActions'
import { DuplicateQuoteButton } from './elements/DuplicateQuoteButton'
import { EditQuoteButton } from './elements/EditQuoteButton'
import { QuoteActionsMenu } from './elements/QuoteActionsMenu'
import { ShareQuoteMenu } from './elements/ShareQuoteMenu'
import { useQuoteViewActions } from './hooks/useQuoteViewActions'

const quoteActionTypeToComponent: Partial<Record<QuoteActionType, ComponentType>> = {
  [QuoteActionType.Share]: ShareQuoteMenu,
  [QuoteActionType.ConvertToInvoice]: ConvertQuoteActions,
  [QuoteActionType.Duplicate]: DuplicateQuoteButton,
  [QuoteActionType.Edit]: EditQuoteButton,
}

const renderQuoteActionComponent = (actionType: QuoteActionType): ReactElement | null => {
  const ActionComponent = quoteActionTypeToComponent[actionType]
  return ActionComponent ? <ActionComponent key={actionType} /> : null
}

export const QuoteViewActions = (): ReactElement => {
  const navigate = useNavigate()
  const { quoteActionsDisplayedAsButtons } = useQuoteViewActions()
  const { track } = useSegment()
  const { quote } = useQuote()
  const { organization } = useUserOrganization()

  const handleConfirm = useCallback(() => {
    track('XXX - Invoicing Core - Quote View - Delete Quote clicked')
  }, [track])

  const handleSuccess = useCallback(() => {
    navigate(getOrganizationViewUrl(organization?.url, reactRoute.organization.quotes.getMainRoute()))
  }, [navigate, organization?.url])

  let relatedDraftInvoiceType: QuoteRelatedDraftInvoiceType | undefined
  if (getQuoteDepositInvoiceDraft(quote)) {
    relatedDraftInvoiceType = QuoteRelatedDraftInvoiceType.DepositInvoice
  } else if (getQuoteRegularInvoiceDraft(quote)) {
    relatedDraftInvoiceType = QuoteRelatedDraftInvoiceType.Invoice
  }

  return (
    <>
      <ButtonsGroup>
        {quoteActionsDisplayedAsButtons.map(renderQuoteActionComponent)}
        <QuoteActionsMenu />
      </ButtonsGroup>
      {quote && (
        <DeleteQuoteModal
          id={ModalId.QuoteDeleteConfirmationModal}
          onConfirm={handleConfirm}
          onSuccess={handleSuccess}
          quoteDocumentType={quote.documentType}
          quoteId={quote.id}
          relatedDraftInvoiceType={relatedDraftInvoiceType}
        />
      )}
    </>
  )
}
